import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, money } from "@/lib/api";
import { track } from "@/lib/analytics";
import { metaEvent } from "@/lib/tracking";

const NextPayment = ({ next }) => {
  const [secs, setSecs] = useState(6);
  useEffect(() => {
    if (secs <= 0) {
      window.location.href = next.url;
      return;
    }
    const t = setTimeout(() => setSecs(secs - 1), 1000);
    return () => clearTimeout(t);
  }, [secs, next.url]);
  return (
    <div data-testid="next-payment-card" className="mt-8 rounded-2xl border border-[var(--accent)]/40 bg-[var(--accent)]/[0.06] p-5">
      <p className="font-display font-bold text-lg">Next: pay for {next.first_name}</p>
      <p className="mt-1 text-sm text-slate-300">Taking you to secure payment in {Math.max(secs, 0)}s…</p>
      <a href={next.url} data-testid="next-payment-button" className="btn-primary mt-4 inline-flex">Pay for {next.first_name} now</a>
    </div>
  );
};

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking" });

  useEffect(() => {
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ phase: "paid", data });
          localStorage.removeItem("epl_reg_draft");
          const key = `epl_tracked_${sessionId}`;
          if (!sessionStorage.getItem(key)) {
            sessionStorage.setItem(key, "1");
            const value = data.amount_cents / 100;
            metaEvent("Purchase", { contentId: data.camp_slug, value, numItems: data.players.length }, data.purchase_event_id);
            track("purchase", { value, currency: "AUD", transaction_id: data.purchase_event_id, num_players: data.players.length });
          }
          return;
        }
        if (["expired", "failed", "cancelled"].includes(data.payment_status)) return setState({ phase: "failed" });
      } catch (e) {
        return setState({ phase: "failed" });
      }
      if (++tries < 20) timer = setTimeout(poll, 2000);
      else setState({ phase: "timeout" });
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="min-h-screen max-w-xl mx-auto px-5 py-24">
      {state.phase === "checking" && <p data-testid="payment-checking" className="flex items-center gap-3 text-slate-300"><Loader2 className="animate-spin accent-text" />Confirming your payment…</p>}
      {state.phase === "paid" && (
        <div data-testid="payment-confirmed">
          <CheckCircle2 size={44} className="accent-text" />
          <h1 className="mt-6 font-display font-bold text-4xl tracking-tight">You're registered</h1>
          <p className="mt-3 text-slate-400">Payment of {money(state.data.amount_cents)} received. Confirmation emails are on their way.</p>
          <div className="mt-8 space-y-3">
            {state.data.players.map((p) => (
              <div key={p.reg_number} data-testid={`confirmed-player-${p.reg_number}`} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 flex justify-between">
                <div><p className="font-display font-bold text-lg">{p.first_name}</p><p className="text-sm accent-text">{p.group_name}</p></div>
                <p className="font-mono-epl text-sm text-slate-300">{p.reg_number}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-slate-400">Session times will be emailed by 25 November 2026.</p>
          {state.data.next && <NextPayment next={state.data.next} />}
          <Link to="/" className="btn-ghost mt-8" data-testid="payment-home-link">Back to home</Link>
        </div>
      )}
      {["failed", "timeout"].includes(state.phase) && (
        <div data-testid="payment-not-confirmed">
          <h1 className="font-display font-bold text-3xl">{state.phase === "failed" ? "Payment not completed" : "Still confirming"}</h1>
          <p className="mt-3 text-slate-400">{state.phase === "failed" ? "Your checkout expired or failed and no payment was taken." : "We haven't received confirmation yet. If you were charged, you'll receive an email shortly."}</p>
          <Link to="/register" className="btn-primary mt-8">Return to registration</Link>
        </div>
      )}
    </div>
  );
}
